import Quill from "quill";

// 优化类型定义
interface DeltaOperation {
  insert: string | Record<string, unknown>;
  attributes?: Record<string, unknown>;
}

interface Delta {
  ops: DeltaOperation[];
}

// 变量选择器的正则表达式 - 匹配格式：${数字-任意内容}
const VARIABLE_SELECTOR_REGEX = /\$\{([1235])-([^}]*)\}/g;

/**
 * 将 Delta 格式转换为字符串
 * @param delta Delta 对象
 * @returns 转换后的字符串
 */
export const transformDeltaToString = (delta: any): string => {
  if (!delta?.ops?.length) {
    return "";
  }

  return delta.ops.reduce((result, op) => {
    const insertValue =
      typeof op.insert === "string"
        ? op.insert
        : Object.values(op.insert).join("");
    return result + insertValue;
  }, "");
};

/**
 * 将字符串转换为 Delta 格式
 * @param text 输入字符串
 * @returns Delta 格式的操作数组
 */
const QuillDelta = Quill.import("delta");
export const transformStringToDelta = (text: string): DeltaOperation[] => {
  if (!text) {
    return [];
  }

  const delta = new QuillDelta();
  const remainingText = text;
  let lastIndex = 0;

  // 使用正则表达式的 exec 方法来找到所有匹配
  const regex = new RegExp(VARIABLE_SELECTOR_REGEX);
  let match;

  while ((match = regex.exec(remainingText)) !== null) {
    // 处理变量选择器前的普通文本
    const beforeText = remainingText.slice(lastIndex, match.index);
    if (beforeText) {
      delta.insert(beforeText);
    }

    // 处理变量选择器
    delta.insert({
      variableSelector: match[0],
    });

    lastIndex = regex.lastIndex;
  }

  // 处理最后剩余的普通文本
  const remainingNormalText = remainingText.slice(lastIndex);
  if (remainingNormalText) {
    delta.insert(remainingNormalText);
  }

  return delta as any;
};

/**
 * 检查文本是否包含变量选择器
 * @param text 要检查的文本
 * @returns 是否包含变量选择器
 */
export const hasVariableSelector = (text: string): boolean => {
  return VARIABLE_SELECTOR_REGEX.test(text);
};

/**
 * 从 Delta 中提取所有变量选择器
 * @param delta Delta 对象
 * @returns 变量选择器数组
 */
export const extractVariableSelectors = (delta: Delta): string[] => {
  if (!delta?.ops?.length) {
    return [];
  }

  return delta.ops
    .filter(
      (op) => typeof op.insert !== "string" && "variableSelector" in op.insert,
    )
    .map((op) => (op.insert as Record<string, string>).variableSelector);
};
